import { createQueryController } from '@tanstack/lit-query';
import {
    type PredefinedQuestion,
    videoChatConfigurationQueryOptions,
    videoDynamicQuestionsQueryOptions,
} from 'Api/chatConfiguration';
import { queryClient } from 'Api/query-client';
import { MediaQueryController } from 'Components/controllers/media-query-controller';
import 'Components/vm-button/vm-button';
import 'Components/vm-icon/vm-icon';
import { html, LitElement } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import vumediAiLogoSrc from 'Static/gfx/vumedi-ai-logo.v2.svg';
import { DESKTOP_BREAKPOINT_QUERY } from 'Utils/viewport';

import '../../../../app/(main)/(content_sidebar_layout)/video/[slug]/_components/vm-video-autosize-bottom-sheet/vm-video-autosize-bottom-sheet';
import type { VmVideoAutosizeBottomSheet } from '../../../../app/(main)/(content_sidebar_layout)/video/[slug]/_components/vm-video-autosize-bottom-sheet/vm-video-autosize-bottom-sheet';
import Styles from './vm-chat-mobile-trigger.scss';

const SUMMARIZE_QUESTION = 'Summarize this video';

const CTA_QUESTION: PredefinedQuestion = { id: 31, question: 'Ask your own question', type: 'cta' };

const LOADING_SKELETON_COUNT = 3;

export interface ChatMobileTriggerSelectDetail {
    question: string;
    type: string;
}

export interface ChatMobileTriggerCloseDetail {
    /** The user closed the sheet, as opposed to it closing because the viewport grew to desktop. */
    dismissed: boolean;
}

/**
 * Mobile strip of chat suggestions under the video player. Selecting one opens a bottom
 * sheet docked under the player; the chat itself is supplied by the consumer as light-DOM
 * children, which are only rendered while the sheet is open.
 *
 * The sheet leaves the strip uncovered, so the consumer sets `streaming` while an answer
 * streams to keep a second question from being asked mid-answer.
 *
 * @slot - Sheet body, i.e. the chat.
 *
 * @fires vm-chat-mobile-trigger:select - A suggestion was picked. Fires once the sheet is
 *   rendered, so listeners can move focus into the slotted chat.
 * @fires vm-chat-mobile-trigger:close - The sheet closed, either dismissed by the user or
 *   because the viewport grew to desktop, where the strip is hidden.
 */
@customElement('vm-chat-mobile-trigger')
export class VmChatMobileTrigger extends LitElement {
    static override styles = Styles;

    @property({ type: Number, attribute: 'video-id' })
    videoId?: number;

    @property({ type: Boolean, reflect: true })
    streaming = false;

    @query('vm-video-autosize-bottom-sheet')
    private _bottomSheet?: VmVideoAutosizeBottomSheet;

    @state()
    private _sheetOpen = false;

    // The strip is hidden from desktop up, hide the sheet as well and show the question in desktop VAIA
    private _desktop = new MediaQueryController(this, DESKTOP_BREAKPOINT_QUERY, (matches) => {
        if (matches) this._sheetOpen = false;
    });

    private _chatConfigurationQuery = createQueryController(
        this,
        () => videoChatConfigurationQueryOptions(this.videoId),
        queryClient,
    );

    private _dynamicQuestionsQuery = createQueryController(
        this,
        // This is dependant on chat config because some videos have disabled VAIA
        //  and this is an expensive operation that is wasted if the chat is not enabled
        () => videoDynamicQuestionsQueryOptions(this._chatConfigurationQuery.current.data ? this.videoId : undefined),
        queryClient,
    );

    /** Anchor for the VAIA product tour, which can't reach into the shadow root by selector. */
    get firstSuggestion(): HTMLElement | null {
        return this.renderRoot.querySelector('.vm-chat-mobile-trigger__suggestion');
    }

    private async _select(question: PredefinedQuestion) {
        this._sheetOpen = true;
        // Wait for the sheet (and so the slotted chat) to render. Still within the click's
        // microtask checkpoint, so iOS lets a listener focus the chat input and raise the keyboard.
        await this.updateComplete;
        await this._bottomSheet?.updateComplete;

        this.dispatchEvent(
            new CustomEvent<ChatMobileTriggerSelectDetail>('vm-chat-mobile-trigger:select', {
                detail: { question: question.question, type: question.type },
                bubbles: true,
                composed: true,
            }),
        );
    }

    private _onSheetClose() {
        this._sheetOpen = false;
        this.dispatchEvent(
            new CustomEvent<ChatMobileTriggerCloseDetail>('vm-chat-mobile-trigger:close', {
                detail: { dismissed: !this._desktop.matches },
                bubbles: true,
                composed: true,
            }),
        );
    }

    private _renderQuestion(question: PredefinedQuestion, index: number) {
        return html`
            <vm-button
                type="button"
                variant="outline"
                color-scheme="neutral"
                size="sm"
                id=${index + 1}
                class="vm-chat-mobile-trigger__suggestion"
                data-type=${question.type}
                ?disabled=${this.streaming}
                @click=${() => this._select(question)}
            >
                ${question.question}
            </vm-button>
        `;
    }

    private _renderSuggestions() {
        const predefinedQuestions = this._chatConfigurationQuery.current.data?.predefined_questions ?? [];
        const summarize = predefinedQuestions.filter((question) => question.question === SUMMARIZE_QUESTION);
        const { status, data } = this._dynamicQuestionsQuery.current;

        if (status === 'pending') {
            return [
                ...summarize.map((question, index) => this._renderQuestion(question, index)),
                ...Array.from(
                    { length: LOADING_SKELETON_COUNT },
                    () => html`<div class="vm-chat-mobile-trigger__skeleton" aria-hidden="true"></div>`,
                ),
            ];
        }

        const dynamicQuestions = (data?.questions ?? []).map((question) => ({
            id: question.id,
            question: question.question,
            type: question.question_source,
        }));

        // Errored or empty dynamic questions fall back to the full config list
        const questions = dynamicQuestions.length ? [...summarize, ...dynamicQuestions] : predefinedQuestions;

        return [...questions, CTA_QUESTION].map((question, index) => this._renderQuestion(question, index));
    }

    override render() {
        return html`<div
                class="vm-chat-mobile-trigger__suggestions"
                aria-busy=${this._dynamicQuestionsQuery.current.isPending}
            >
                <vm-icon size="sm" name="sparkle"></vm-icon>
                ${this._renderSuggestions()}
            </div>
            <vm-video-autosize-bottom-sheet
                class="vm-chat-mobile-trigger__sheet"
                disable-backdrop
                .open=${this._sheetOpen}
                @vm-close=${this._onSheetClose}
            >
                <img slot="header" class="vm-chat-mobile-trigger__logo" src=${vumediAiLogoSrc} alt="Vumedi AI" />
                <slot></slot>
            </vm-video-autosize-bottom-sheet>`;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'vm-chat-mobile-trigger': VmChatMobileTrigger;
    }
    interface HTMLElementEventMap {
        'vm-chat-mobile-trigger:select': CustomEvent<ChatMobileTriggerSelectDetail>;
        'vm-chat-mobile-trigger:close': CustomEvent<ChatMobileTriggerCloseDetail>;
    }
    interface DocumentEventMap {
        'vm-chat-mobile-trigger:select': CustomEvent<ChatMobileTriggerSelectDetail>;
        'vm-chat-mobile-trigger:close': CustomEvent<ChatMobileTriggerCloseDetail>;
    }
}
